import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import HowItWorks from '../content/HowItWorks';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import BbqFaq from '../faqs/BbqFaq';
import CateringCta from '../content/CateringCta';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

function BbqCatering() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  const { content: cms } = usePageContent('catering_bbq');

  const featuredTrucks = { food_categories: 'bbq' };

  return (
    <div className='CateringPage'>
      <Meta
        title={cms?.meta?.title || `BBQ Food ${vehicle_type} Catering in ${city_name}`}
        type='website'
        canonical='/food-truck-catering/bbq-catering'
        description={cms?.meta?.description || `Book BBQ food ${vehicleType} catering in ${city_name}. Brisket, ribs, pulled pork, and smoked sausage. Compare quotes from local BBQ ${vehicleType}s.`}
        image={cdnPhoto(`${citySlug}-bbq-food-truck-catering`)}
        structuredData={{
          cateringService: {
            name: `BBQ Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book BBQ food ${vehicleType} catering in ${city_name}. Smoked brisket, ribs, pulled pork and more.`,
            url: '/food-truck-catering/bbq-catering',
            serviceType: 'BBQ Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'BBQ Catering', item: '/food-truck-catering/bbq-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-bbq-food-truck-catering.webp`} alt='BBQ food truck catering at an event' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `BBQ food ${vehicleType} catering in ${city_name}`}</h1>
            <CmsRichText
              html={cms?.hero?.subheadline}
              fallback={
                <p>Brisket, ribs, pulled pork, and smoked sausage. Tell us about the event and compare quotes from BBQ {vehicleType}s that can do it.</p>
              }
            />
            <p>
              <Link to='/catering/requests/new' className='Button Button--mobile-w100'>{cms?.hero?.cta_label || 'Get BBQ catering quotes'}</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='BBQ'
        image={`${CDN_BASE}/photos/${citySlug}-bbq-food-truck-menu.webp`}
        optionsText='Brisket, ribs, pulled pork, smoked sausage, and sides.'
      />

      <FeaturedTrucks title={`BBQ food ${vehicleType}s`} options={featuredTrucks} />

      <WhyBookWithUs />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What to expect from a BBQ {vehicleType}</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-smoked-bbq-food-truck.webp`} alt='Smoked BBQ served from a food truck' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Styles and menus</h4>
            <p>
              Texas-style brisket and sausage, Carolina pulled pork, Kansas City burnt ends, or a mix. Most {vehicleType}s run their own smokers and bring house sauces and sides. Check each {vehicleType}&#39;s public profile for the menu.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-bbq-catering-event.webp`} alt='BBQ catering setup at an outdoor event' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>How service works</h4>
            <p>
              Smoked meat is cooked ahead and plates fast, which is why BBQ works for a <Link to='/food-truck-catering/corporate-catering'>company event</Link>, a <Link to='/food-truck-catering/community-events'>festival</Link>, or a big <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>. Guests order at the window, or the {vehicleType} sets up a buffet line for a <Link to='/food-truck-catering/wedding-catering'>wedding</Link> if the venue allows. Brisket takes hours, so ask how far ahead the {vehicleType} needs a final guest count.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-bbq-food-truck-whole-hog.webp`} alt='Whole hog roast from a BBQ food truck' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Dietary options</h4>
            <p>
              Many {vehicleType}s offer vegetarian or plant-based options and gluten-free sides. Note what you need in your request so quotes reflect it.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title='What customers say' />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <BbqFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default BbqCatering;
